import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Divider,
  Box,
  Typography,
  Chip,
  Collapse,
  alpha,
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FiberManualRecord,
} from '@mui/icons-material';

import { useAuth, useRole } from '../../contexts/AuthContext';
import { getNavigationForRole } from '../../utils/navigation';
import type { NavigationItem } from "../../types";

interface SidebarProps {
  open: boolean;
  width: number;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, width, onClose }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { hasAnyRole } = useRole();
  const navigate = useNavigate();
  const location = useLocation();

  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  // Получаем навигационные элементы для роли пользователя
  const navigationItems = user ? getNavigationForRole(user.role) : [];

  const handleNavigation = (path: string) => {
    navigate(path);
    // На мобильных устройствах закрываем sidebar после навигации
    if (window.innerWidth < 1024) {
      onClose();
    }
  };

  const handleToggleExpand = (itemLabel: string) => {
    setExpandedItems(prev => 
      prev.includes(itemLabel)
        ? prev.filter(item => item !== itemLabel)
        : [...prev, itemLabel]
    );
  };

  const isItemActive = (item: NavigationItem): boolean => {
    return location.pathname === item.path || 
           (item.children && item.children.some(child => location.pathname === child.path));
  };

  const isChildActive = (childPath: string): boolean => {
    return location.pathname === childPath;
  };

  const renderNavigationItem = (item: NavigationItem, level = 0) => {
    const isActive = isItemActive(item);
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.label);
    const IconComponent = item.icon;

    return (
      <React.Fragment key={item.path}>
        <ListItem disablePadding sx={{ pl: level * 2 }}>
          <ListItemButton
            selected={isActive && !hasChildren}
            onClick={hasChildren ? () => handleToggleExpand(item.label) : () => handleNavigation(item.path)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              '&.Mui-selected': {
                backgroundColor: alpha('#1976d2', 0.1),
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: alpha('#1976d2', 0.15),
                },
              },
              '&:hover': {
                backgroundColor: alpha('#1976d2', 0.05),
              },
            }}
          >
            <ListItemIcon
              sx={{
                color: isActive ? 'primary.main' : 'text.secondary',
                minWidth: 40,
              }}
            >
              <IconComponent fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t(item.label)}
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: isActive ? 600 : 400,
              }}
            />
            {hasChildren && (
              isExpanded ? <ExpandLess /> : <ExpandMore />
            )}
          </ListItemButton>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children!.map((child) => renderNavigationItem(child, level + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar />
      
      {/* Информация о пользователе */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {t('auth.welcome')}
        </Typography>
        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
          {user?.full_name || user?.username}
        </Typography>
        <Chip
          label={t(`users.userRoles.${user?.role || 'viewer'}`)}
          size="small"
          color="primary"
          variant="outlined"
        />
      </Box>

      {/* Основная навигация */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <List sx={{ pt: 1 }}>
          {navigationItems.map((item) => renderNavigationItem(item))}
        </List>
      </Box>

      {/* Футер сайдбара */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Digital Signage v1.0.0
        </Typography>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          © 2025
        </Typography>
      </Box>
    </Box>
  );

  return (
    <>
      {/* Desktop Drawer */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          display: { xs: 'none', md: 'block' },
          width: open ? width : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>

      {/* Mobile Drawer */}
      <Drawer
        variant="temporary"
        anchor="left"
        open={open}
        onClose={onClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width,
            boxSizing: 'border-box',
            backgroundColor: 'background.paper',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Sidebar; 
 
 
 